import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, Volume2, VolumeX, Play,
  Truck, MapPin, Wrench, Clock, Shield, ArrowRight, Check, Download,
  Receipt, Zap, BarChart3, Route, Bot, ChevronDown, Users,
  Fuel, Calculator, TrendingUp, Apple, Smartphone
} from 'lucide-react';
import { QuoteCalculator } from './components/QuoteCalculator';

// ==================== SLIDE DATA ====================

const SLIDES = [
  {
    id: 'intro',
    title: 'Decouvrez ProPilote',
    subtitle: 'La plateforme tout-en-un pour le transport routier',
    text: "Que vous soyez proprietaire-operateur, gestionnaire de flotte ou atelier mecanique, ProPilote vous offre les outils dont vous avez besoin pour gagner du temps, rester conforme et augmenter votre rentabilite.",
    image: '/slides/hero_transport.png',
    icon: Truck,
    color: '#f59e0b',
    bgGradient: 'from-slate-950 via-slate-900 to-amber-950/30',
  },
  {
    id: 'driver_app',
    title: 'Application Chauffeur',
    subtitle: 'Le compagnon de route de vos conducteurs',
    text: "Rapports journaliers, scan de documents, feuille de route, gestion des arrets. Tout est concu pour etre simple, rapide et accessible meme sur la route. Fini les piles de papier.",
    image: '/slides/slide_driver_mobile.png',
    screenshot: '/slides/slide_driver_dashboard.jpg',
    icon: MapPin,
    color: '#3b82f6',
    bgGradient: 'from-slate-950 via-slate-900 to-blue-950/30',
    features: ['Rapports journaliers', 'Scan de documents', 'Feuille de route', 'GPS en temps reel'],
  },
  {
    id: 'dispatch',
    title: 'Repartition & Dispatch',
    subtitle: 'Vos operations en temps reel',
    text: "Creez des commandes, assignez-les par glisser-deposer, suivez la progression heure par heure. Vos chauffeurs recoivent une notification sonore instantanee. Plus besoin d'appeler.",
    screenshot: '/slides/slide_dispatch.jpg',
    icon: MapPin,
    color: '#8b5cf6',
    bgGradient: 'from-slate-950 via-slate-900 to-violet-950/30',
    features: ['Cueillette & livraison', 'Glisser-deposer', 'Notifications instantanees', 'Suivi temps reel'],
  },
  {
    id: 'maintenance',
    title: 'Entretien & Inspections',
    subtitle: 'Gardez votre flotte en parfait etat',
    text: "Suivez les inspections obligatoires, enregistrez les mesures de freins et pneus, recevez des alertes automatiques. Restez conforme et prevenez les pannes couteuses.",
    image: '/slides/slide_inspection_visual.png',
    screenshot: '/slides/slide_maintenance.jpg',
    icon: Wrench,
    color: '#10b981',
    bgGradient: 'from-slate-950 via-slate-900 to-emerald-950/30',
    features: ['Inspections SAAQ', 'Mesures freins/pneus', 'Alertes automatiques', 'Historique complet'],
  },
  {
    id: 'fleet',
    title: 'Gestion de Flotte',
    subtitle: 'Toutes vos informations centralisees',
    text: "Numero d'unite, plaque, marque, modele, odometre, historique. Gerez vos camions et remorques dans une interface claire et intuitive.",
    screenshot: '/slides/slide_fleet.jpg',
    icon: Truck,
    color: '#f59e0b',
    bgGradient: 'from-slate-950 via-slate-900 to-amber-950/30',
    features: ['Camions & remorques', 'Historique complet', 'Odometre & plaque', 'Ajout en quelques clics'],
  },
  {
    id: 'hours',
    title: 'Heures de Service',
    subtitle: 'Conformite reglementaire automatisee',
    text: "Calcul automatique des cycles de conduite, heures restantes, alertes de depassement. Vue claire sur la disponibilite pour faciliter la planification.",
    image: '/slides/slide_hours_visual.png',
    icon: Clock,
    color: '#ef4444',
    bgGradient: 'from-slate-950 via-slate-900 to-red-950/30',
    features: ['Cycles automatiques', 'Heures restantes', 'Alertes depassement', 'Planification facilitee'],
  },
  {
    id: 'accounting',
    title: 'Comptabilite Intelligente',
    subtitle: 'Scanner IA pour vos factures et depenses',
    text: "Photographiez vos factures et recus avec votre telephone. L'intelligence artificielle extrait automatiquement le fournisseur, les montants, les taxes TPS/TVQ et categorise la depense. Fini la saisie manuelle.",
    image: '/slides/slide_accounting_ai.png',
    icon: Receipt,
    color: '#06b6d4',
    bgGradient: 'from-slate-950 via-slate-900 to-cyan-950/30',
    features: ['Scan IA automatique', 'Extraction TPS/TVQ', 'Donnees IFTA carburant', 'Fournisseurs appris'],
  },
  {
    id: 'pricing',
    title: 'Nos Forfaits',
    subtitle: 'Un plan adapte a votre entreprise',
    text: "Commencez avec un essai gratuit de 30 jours. Sans engagement, sans carte de credit.",
    icon: Shield,
    color: '#f59e0b',
    bgGradient: 'from-slate-950 via-slate-900 to-amber-950/30',
    isPricing: true,
  },
];

const PLANS = [
  {
    id: 'chauffeur',
    name: 'App Chauffeur',
    price: '0',
    priceSuffix: 'Gratuit',
    target: 'Chauffeurs indépendants',
    vehicles: 'Application mobile personnelle',
    extra: 'iOS · Android · Web',
    features: [
      'Rapports quotidiens',
      'Inspections véhicule',
      'Heures de service',
      'Scan documents (BOL)',
      'Signatures livraison',
      'Historique personnel',
    ],
    popular: false,
    isFree: true,
    showDownloadLinks: true,
    icon: Users,
  },
  {
    id: 'localisation',
    name: 'Localisation',
    price: '39.99',
    target: 'Flottes moyennes',
    vehicles: '5 véhicules inclus',
    extra: '+3$/véhicule supplémentaire',
    features: [
      'Tout de l\'App Chauffeur',
      'GPS temps réel',
      'Historique des trajets',
      'Carte en direct',
      'Suivi 24/7 (background)',
      'Alertes de position',
    ],
    popular: false,
    icon: MapPin,
  },
  {
    id: 'repartition',
    name: 'Répartition',
    price: '69.99',
    target: 'Flottes avec dispatch',
    vehicles: '5 véhicules inclus',
    extra: '+3$/véhicule supplémentaire',
    features: [
      'Tout de Localisation',
      'Kanban de répartition',
      'Assignation d\'ordres',
      'Portail clients',
      'Notifications temps réel',
      'Multi-repartiteurs',
    ],
    popular: true,
    icon: Truck,
  },
  {
    id: 'entretien',
    name: 'Entretien',
    price: '19.99',
    target: 'Propriétaires-opérateurs',
    vehicles: '5 véhicules inclus',
    extra: '+3$/véhicule supplémentaire',
    features: [
      'Gestion de flotte',
      'Inspections SAAQ',
      'Mesures freins & pneus',
      'Alertes email',
      'Historique mesures',
      'Graphiques de tendance',
    ],
    popular: false,
    icon: Wrench,
  },
  {
    id: 'facturation',
    name: 'Facturation',
    price: '99.99',
    target: 'Grandes flottes',
    vehicles: '5 véhicules inclus',
    extra: '+3$/véhicule supplémentaire',
    features: [
      'Tout de Répartition',
      'Facturation automatique',
      'Rapports financiers',
      'Scanner IA (TPS/TVQ)',
      'Comptabilité intégrée',
      'Export comptable',
    ],
    popular: false,
    icon: Receipt,
  },
];

// Plans additionnels affichables via "Voir tous les forfaits"
const PLANS_EXTRA = [
  {
    id: 'atelier',
    name: 'Atelier Indépendant',
    price: '19.99',
    target: 'Ateliers mécaniques',
    vehicles: '20 véhicules inclus',
    extra: '+1$/véhicule supplémentaire',
    features: [
      'Gestion multi-clients',
      'Bons de travail',
      'Inspections SAAQ',
      'Mesures freins & pneus',
      'Historique par véhicule',
      'Facturation atelier',
    ],
    icon: Wrench,
  },
  {
    id: 'pro',
    name: 'ProPilote Pro',
    price: '149.99',
    target: 'Entreprises tout-en-un',
    vehicles: '5 véhicules inclus',
    extra: '+3$/véhicule supplémentaire',
    features: [
      'Accès complet à tous les modules',
      'Entretien + Localisation + Répartition',
      'Facturation + Dépenses',
      'Support prioritaire',
      'API accès',
      'Formation incluse',
    ],
    isBestValue: true,
    icon: Shield,
  },
  {
    id: 'depenses',
    name: 'Gestion des Dépenses',
    price: '14.99',
    priceSuffix: '/mois - Module complémentaire',
    target: 'Tout utilisateur payant',
    vehicles: 'Aucun véhicule requis',
    extra: 'S\'ajoute a votre plan actuel',
    features: [
      'Suivi dépenses par chauffeur',
      'Dépenses par véhicule',
      'Scan de reçus',
      'Rapports de dépenses',
      'Export IFTA carburant',
    ],
    isAddon: true,
    icon: Receipt,
  },
  {
    id: 'logistique-plus',
    name: 'Logistique Plus',
    price: '29.00',
    priceSuffix: '/mois de base + sièges',
    target: 'PME/Grandes entreprises',
    vehicles: 'Utilisateurs multiples',
    extra: 'Workflow Représentant -> SAC -> Répartiteur',
    features: [
      'Workflow multi-niveaux',
      'Rôle Agent des ventes',
      'Rôle Service client (SAC)',
      'Multi-repartiteurs',
      'Kanban SAC',
      'Audit trail complet',
    ],
    isAddon: true,
    icon: Users,
  },
];

// ==================== VISION 2.0 DATA ====================

const VISION_FEATURES = [
  {
    icon: Bot,
    title: 'Rate-Agent IA',
    description: 'Tarification intelligente par conversation naturelle. Calculez vos surcharges carburant, ajustez vos prix par client, recevez des alertes predictives.',
    color: '#10b981',
    status: '✓ Disponible',
    statusType: 'live',
  },
  {
    icon: Route,
    title: 'Optimisation de Routes',
    description: 'Routes optimales automatiques en temps réel. Minimisez les couts carburant, maximisez les livraisons a temps, reduisez les emissions CO2.',
    color: '#3b82f6',
    status: 'Bientot - Q2 2026',
    statusType: 'soon',
  },
  {
    icon: BarChart3,
    title: 'Analytics & KPIs',
    description: 'Tableau de bord avec indicateurs en temps réel. Revenus, marges, couts carburant, utilisation flotte, livraisons a temps.',
    color: '#10b981',
    status: '✓ Disponible',
    statusType: 'live',
  },
];

// ==================== SLIDE CONTENT ====================

const SlideContent = ({ slide, isActive }) => {
  const navigate = useNavigate();

  if (slide.isPricing) {
    return <PricingSlide isActive={isActive} />;
  }

  return (
    <div className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 w-full max-w-6xl mx-auto px-4 sm:px-8 transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="flex-1 space-y-5 text-center lg:text-left">
        <div className="flex items-center gap-3 justify-center lg:justify-start">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${slide.color}22` }}>
            <slide.icon size={22} style={{ color: slide.color }} />
          </div>
          <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: slide.color }}>
            {slide.subtitle}
          </span>
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
          {slide.title}
        </h2>

        <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
          {slide.text}
        </p>

        {slide.features && (
          <div className="grid grid-cols-2 gap-2 pt-2">
            {slide.features.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                <Check size={14} style={{ color: slide.color }} className="shrink-0" />
                {f}
              </div>
            ))}
          </div>
        )}

        {slide.id === 'driver_app' && (
          <div className="pt-3 flex items-center gap-4 justify-center lg:justify-start" data-testid="driver-app-cta">
            <div
              className="shrink-0 rounded-[22px] overflow-hidden shadow-2xl ring-1 ring-white/10"
              style={{ width: 88, height: 88, background: '#0B1F3A' }}
              data-testid="driver-app-icon"
            >
              <img
                src="/icons/icon-192.png"
                alt="Icône de l'application ProPilote Chauffeur"
                className="w-full h-full object-cover"
                width="88"
                height="88"
              />
            </div>
            <div className="flex flex-col items-start gap-1.5">
              <span className="text-xs font-semibold tracking-widest uppercase text-amber-400">
                Votre future app
              </span>
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
                data-testid="driver-app-download-btn"
              >
                <Download size={16} />
                Télécharger l'app chauffeur gratuit
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center gap-4 w-full max-w-lg">
        {slide.image && (
          <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10">
            <img src={slide.image} alt={slide.title} className="w-full h-auto" loading="lazy" />
          </div>
        )}
        {slide.screenshot && (
          <div className="w-full rounded-xl overflow-hidden shadow-xl border border-white/10 relative">
            <div className="absolute top-0 left-0 right-0 h-7 bg-slate-800 flex items-center px-3 gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              <span className="text-[10px] text-slate-400 ml-2">propilote.ca</span>
            </div>
            <img src={slide.screenshot} alt={`${slide.title} - Interface`} className="w-full h-auto pt-7" loading="lazy" />
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PRICING SLIDE ====================

export const PricingSlide = ({ isActive }) => {
  const navigate = useNavigate();
  const [showAllPlans, setShowAllPlans] = useState(false);

  return (
    <div className={`w-full max-w-7xl mx-auto px-4 sm:px-8 transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3">
          Nos Forfaits
        </h2>
        <p className="text-slate-400 text-lg">
          Essai gratuit de 30 jours. Sans engagement, sans carte de crédit.
        </p>
      </div>

      {/* Ligne 1 : 5 forfaits principaux */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-7xl mx-auto">
        {PLANS.map((plan) => {
          const PlanIcon = plan.icon || Truck;
          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-5 border transition-all duration-300 hover:scale-[1.03] flex flex-col ${
                plan.popular
                  ? 'bg-amber-500/10 border-amber-500/50 shadow-xl shadow-amber-500/20'
                  : plan.isFree
                    ? 'bg-blue-500/5 border-blue-500/30'
                    : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  POPULAIRE
                </div>
              )}
              {plan.isFree && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  GRATUIT
                </div>
              )}

              {/* Header avec icône */}
              <div className="flex items-start gap-3 mb-3">
                {plan.isFree ? (
                  <div
                    className="shrink-0 rounded-xl overflow-hidden shadow-lg ring-1 ring-white/10"
                    style={{ width: 44, height: 44, background: '#0B1F3A' }}
                    data-testid={`plan-${plan.id}-icon`}
                  >
                    <img src="/icons/icon-192.png" alt="ProPilote App" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                    plan.popular ? 'bg-amber-500/20' : 'bg-slate-700/50'
                  }`}>
                    <PlanIcon size={20} className={plan.popular ? 'text-amber-400' : 'text-slate-300'} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-white leading-tight">{plan.name}</h3>
                  <p className="text-slate-400 text-[11px] leading-tight mt-0.5">{plan.target}</p>
                </div>
              </div>

              {/* Prix */}
              <div className="flex items-baseline gap-1 mb-1">
                {plan.isFree ? (
                  <>
                    <span className="text-2xl font-black text-white">Gratuit</span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl font-black text-white">{plan.price}$</span>
                    <span className="text-slate-400 text-xs">/mois</span>
                  </>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mb-3 leading-tight min-h-[28px]">
                {plan.vehicles}
                {plan.extra && !plan.isFree && <> &middot; {plan.extra}</>}
                {plan.isFree && <><br />{plan.extra}</>}
              </p>

              {/* Features */}
              <ul className="space-y-1.5 mb-5 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-300 leading-snug">
                    <Check size={12} className={`mt-0.5 shrink-0 ${plan.popular ? 'text-amber-400' : plan.isFree ? 'text-blue-400' : 'text-emerald-500'}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* Boutons App Chauffeur (téléchargement) */}
              {plan.showDownloadLinks ? (
                <div className="space-y-2">
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-2 rounded-lg font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors flex items-center justify-center gap-2"
                    data-testid={`plan-${plan.id}-signup`}
                  >
                    <Download size={13} />
                    Créer un compte
                  </button>
                  <div className="grid grid-cols-2 gap-1.5">
                    <a
                      href="https://testflight.apple.com/join/e2FrpbJj"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-1.5 rounded-lg text-[10px] font-semibold bg-slate-800 hover:bg-slate-700 text-white transition-colors flex items-center justify-center gap-1 border border-slate-700"
                      data-testid={`plan-${plan.id}-ios`}
                    >
                      <Apple size={11} />
                      iOS
                    </a>
                    <a
                      href="/register"
                      onClick={(e) => { e.preventDefault(); navigate('/register'); }}
                      className="py-1.5 rounded-lg text-[10px] font-semibold bg-slate-800 hover:bg-slate-700 text-white transition-colors flex items-center justify-center gap-1 border border-slate-700"
                      data-testid={`plan-${plan.id}-android`}
                    >
                      <Smartphone size={11} />
                      Web/PWA
                    </a>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/admin/register')}
                  className={`w-full py-2 rounded-lg font-bold text-xs transition-colors ${
                    plan.popular
                      ? 'bg-amber-500 hover:bg-amber-400 text-black'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                  data-testid={`plan-${plan.id}-btn`}
                >
                  Essai gratuit 30 jours
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Toggle : Voir tous les forfaits */}
      <div className="text-center mt-8">
        <button
          onClick={() => setShowAllPlans(!showAllPlans)}
          className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold text-sm transition-colors group"
          data-testid="toggle-all-plans-btn"
        >
          <span>{showAllPlans ? 'Masquer les forfaits complementaires' : 'Voir tous les forfaits'}</span>
          <ChevronDown
            size={16}
            className={`transition-transform ${showAllPlans ? 'rotate-180' : ''} group-hover:translate-y-0.5`}
          />
        </button>
      </div>

      {/* Forfaits complémentaires (expandable) */}
      {showAllPlans && (
        <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-500" data-testid="extra-plans-section">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-white mb-1">Forfaits spécialisés & modules</h3>
            <p className="text-slate-400 text-sm">Pour les cas d'usage spécifiques</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {PLANS_EXTRA.map((plan) => {
              const PlanIcon = plan.icon || Shield;
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-5 border flex flex-col ${
                    plan.isBestValue
                      ? 'bg-emerald-500/5 border-emerald-500/40'
                      : plan.isAddon
                        ? 'bg-purple-500/5 border-purple-500/30'
                        : 'bg-slate-800/50 border-slate-700/50'
                  }`}
                  data-testid={`extra-plan-card-${plan.id}`}
                >
                  {plan.isBestValue && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      MEILLEURE VALEUR
                    </div>
                  )}
                  {plan.isAddon && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      MODULE COMPLEMENTAIRE
                    </div>
                  )}

                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                      plan.isBestValue ? 'bg-emerald-500/20' : plan.isAddon ? 'bg-purple-500/20' : 'bg-slate-700/50'
                    }`}>
                      <PlanIcon size={20} className={
                        plan.isBestValue ? 'text-emerald-400' : plan.isAddon ? 'text-purple-400' : 'text-slate-300'
                      } />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-white leading-tight">{plan.name}</h3>
                      <p className="text-slate-400 text-[11px] leading-tight mt-0.5">{plan.target}</p>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-2xl font-black text-white">{plan.price}$</span>
                    <span className="text-slate-400 text-xs">{plan.priceSuffix || '/mois'}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3 leading-tight">
                    {plan.vehicles} &middot; {plan.extra}
                  </p>

                  <ul className="space-y-1.5 mb-5 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-slate-300 leading-snug">
                        <Check size={12} className={`mt-0.5 shrink-0 ${
                          plan.isBestValue ? 'text-emerald-400' : plan.isAddon ? 'text-purple-400' : 'text-emerald-500'
                        }`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/admin/register')}
                    className={`w-full py-2 rounded-lg font-bold text-xs transition-colors ${
                      plan.isBestValue
                        ? 'bg-emerald-500 hover:bg-emerald-400 text-white'
                        : plan.isAddon
                          ? 'bg-purple-500 hover:bg-purple-400 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                    data-testid={`extra-plan-${plan.id}-btn`}
                  >
                    Essai gratuit 30 jours
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Note bas de page */}
      <div className="text-center mt-8 text-xs text-slate-500 max-w-2xl mx-auto">
        <p>Tous les forfaits incluent : hébergement sécurisé au Canada, sauvegardes quotidiennes, mises à jour automatiques et support par courriel. Tarifs en dollars canadiens (CAD) avant taxes.</p>
      </div>
    </div>
  );
};

// ==================== VISION 2.0 SECTION ====================

const VisionSection = () => (
  <section className="py-20 px-4 sm:px-8 bg-slate-950" data-testid="vision-section">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-2 text-amber-500 text-xs font-bold tracking-widest uppercase mb-4 bg-amber-500/10 px-4 py-2 rounded-full border border-amber-500/20">
          <Zap size={14} />
          Feuille de route 2026
        </span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
          ProPilote evolue avec vous
        </h2>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto">
          Decouvrez les fonctionnalites avancees deja disponibles et ce qui s'en vient.
        </p>
      </div>

      <div className="relative mb-8">
        <div className="w-full max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <img src="/slides/slide_vision_ai.png" alt="Vision ProPilote 2.0" className="w-full h-auto" loading="lazy" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        {VISION_FEATURES.map((feat, i) => (
          <div
            key={i}
            className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-6 hover:border-slate-600 transition-all duration-300 group"
            data-testid={`vision-feature-${feat.statusType}`}
          >
            <div className="absolute top-4 right-4">
              <span
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${
                  feat.statusType === 'live'
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                    : 'bg-slate-800 text-slate-400 border-slate-700'
                }`}
              >
                {feat.status}
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${feat.color}15` }}>
              <feat.icon size={24} style={{ color: feat.color }} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{feat.title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{feat.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ==================== ROI CALCULATOR ====================

const ROICalculator = () => {
  const [trucks, setTrucks] = useState(10);
  const navigate = useNavigate();

  const monthlyBase = trucks <= 5 ? 19.99 : trucks <= 15 ? 49.99 : 49.99 + (trucks - 15) * 2;
  const annualCost = Math.round(monthlyBase * 12);

  // Conservative estimates per truck per year
  const fuelSavings = trucks * 480;        // ~40$/month per truck in fuel optimization
  const adminSavings = trucks * 360;       // ~30$/month admin time saved
  const complianceSavings = trucks * 240;  // ~20$/month avoiding fines/delays
  const totalSavings = fuelSavings + adminSavings + complianceSavings;
  const roi = annualCost > 0 ? Math.round(((totalSavings - annualCost) / annualCost) * 100) : 0;

  return (
    <section className="py-20 px-4 sm:px-8 bg-gradient-to-b from-slate-950 to-slate-900" data-testid="roi-calculator">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
            Calculez votre retour sur investissement
          </h2>
          <p className="text-slate-400 text-lg">
            Estimez vos economies annuelles avec ProPilote
          </p>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 sm:p-8">
          <div className="mb-8">
            <label className="flex items-center justify-between text-sm text-slate-300 mb-3">
              <span>Nombre de vehicules</span>
              <span className="text-2xl font-black text-amber-500">{trucks}</span>
            </label>
            <input
              type="range"
              min="1"
              max="100"
              value={trucks}
              onChange={e => setTrucks(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer accent-amber-500"
              data-testid="roi-slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>1</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100</span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800/60 rounded-xl p-4 text-center">
              <Fuel size={20} className="text-amber-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{fuelSavings.toLocaleString()}$</p>
              <p className="text-xs text-slate-400 mt-1">Economies carburant</p>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 text-center">
              <Calculator size={20} className="text-blue-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{adminSavings.toLocaleString()}$</p>
              <p className="text-xs text-slate-400 mt-1">Temps admin epargne</p>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 text-center">
              <Shield size={20} className="text-emerald-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-white">{complianceSavings.toLocaleString()}$</p>
              <p className="text-xs text-slate-400 mt-1">Conformite & amendes</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
              <TrendingUp size={20} className="text-amber-500 mx-auto mb-2" />
              <p className="text-xl font-bold text-amber-400">{totalSavings.toLocaleString()}$</p>
              <p className="text-xs text-slate-400 mt-1">Economies totales/an</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-700">
            <div className="text-center sm:text-left">
              <p className="text-sm text-slate-400">
                Cout annuel: <span className="text-white font-bold">{annualCost.toLocaleString()}$</span>
                {' '} &middot; ROI: <span className="text-amber-400 font-bold">{roi}%</span>
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/register')}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-xl transition-colors text-sm"
              data-testid="roi-cta-btn"
            >
              Commencer l'essai gratuit <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== FINAL CTA ====================

const FinalCTA = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 px-4 sm:px-8 bg-slate-950" data-testid="final-cta">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
          Pret a transformer votre entreprise?
        </h2>
        <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
          Rejoignez les entreprises de transport qui gerent leur flotte plus efficacement avec ProPilote.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/admin/register')}
            className="flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-4 rounded-xl transition-colors text-base"
            data-testid="final-cta-register"
          >
            Essai gratuit 30 jours <ArrowRight size={18} />
          </button>
          <button
            onClick={() => navigate('/register')}
            className="flex items-center justify-center gap-2 bg-slate-800 border border-slate-700 hover:border-blue-500 text-white font-bold px-8 py-4 rounded-xl transition-colors text-base"
            data-testid="final-cta-driver"
          >
            <Download size={18} />
            App chauffeur gratuit
          </button>
        </div>
        <p className="text-xs text-slate-600 mt-6">Sans carte de credit. Installation en 2 minutes.</p>
      </div>
    </section>
  );
};

// ==================== MAIN PAGE ====================

export default function PresentationPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const audioRef = useRef(null);
  const sectionsRef = useRef(null);
  const navigate = useNavigate();

  const slide = SLIDES[currentSlide];
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === SLIDES.length - 1;

  const playNarration = useCallback(() => {
    if (!audioEnabled) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    const audio = new Audio(`/slides/narration_${slide.id}.mp3`);
    audioRef.current = audio;
    audio.volume = 1.0;
    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
    audio.onpause = () => setIsPlaying(false);
    audio.onerror = () => setIsPlaying(false);
    audio.play().catch(() => setIsPlaying(false));
  }, [audioEnabled, slide.id]);

  useEffect(() => {
    playNarration();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [playNarration]);

  const goNext = () => {
    if (!isLast) setCurrentSlide(prev => prev + 1);
  };

  const goPrev = () => {
    if (!isFirst) setCurrentSlide(prev => prev - 1);
  };

  const toggleAudio = () => {
    if (audioEnabled && audioRef.current) {
      audioRef.current.pause();
    }
    setAudioEnabled(prev => !prev);
  };

  const scrollToSections = () => {
    setShowSections(true);
    setTimeout(() => {
      sectionsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return (
    <div className="bg-slate-950" data-testid="presentation-page">
      {/* ===== SLIDER SECTION ===== */}
      <div className={`min-h-screen bg-gradient-to-br ${slide.bgGradient} flex flex-col transition-all duration-700`}>
        {/* Top bar */}
        <header className="flex items-center justify-between px-4 sm:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center bg-slate-900 ring-1 ring-amber-500/40">
              <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">ProPilote</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleAudio}
              className={`p-2.5 rounded-xl border transition-colors ${
                audioEnabled
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-500'
                  : 'bg-slate-800 border-slate-700 text-slate-500'
              }`}
              data-testid="audio-toggle"
            >
              {audioEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>

            {audioEnabled && !isPlaying && (
              <button
                onClick={playNarration}
                className="p-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-colors"
                data-testid="replay-audio"
              >
                <Play size={18} />
              </button>
            )}

            <button
              onClick={() => navigate('/register')}
              className="hidden sm:flex items-center gap-2 bg-slate-800 border border-slate-700 hover:border-blue-500 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors"
              data-testid="header-driver-app-btn"
            >
              <Download size={15} />
              App chauffeur gratuit
            </button>
            <button
              onClick={() => navigate('/admin/register')}
              className="hidden sm:flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
              data-testid="skip-to-register"
            >
              Essai gratuit <ArrowRight size={16} />
            </button>
          </div>
        </header>

        {/* Slide content */}
        <main className="flex-1 flex items-center justify-center py-6 overflow-hidden">
          <SlideContent slide={slide} isActive={true} />
        </main>

        {/* Bottom navigation */}
        <footer className="px-4 sm:px-8 py-5">
          <div className="flex justify-center gap-2 mb-4">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setCurrentSlide(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === currentSlide ? 'w-8' : 'w-2'
                }`}
                style={{
                  backgroundColor: i === currentSlide ? slide.color : '#334155',
                }}
                data-testid={`dot-${s.id}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between max-w-md mx-auto">
            <button
              onClick={goPrev}
              disabled={isFirst}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-colors ${
                isFirst
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'bg-slate-800 border border-slate-700 text-slate-300 hover:text-white'
              }`}
              data-testid="prev-btn"
            >
              <ChevronLeft size={18} /> Precedent
            </button>

            <span className="text-slate-500 text-xs font-medium">
              {currentSlide + 1} / {SLIDES.length}
            </span>

            {isLast ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={scrollToSections}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-slate-800 border border-slate-700 text-slate-300 hover:text-white transition-colors"
                  data-testid="cta-discover-more"
                >
                  En savoir plus <ChevronDown size={16} />
                </button>
                <button
                  onClick={() => navigate('/admin/register')}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm bg-amber-500 hover:bg-amber-400 text-black transition-colors"
                  data-testid="cta-start-trial"
                >
                  Commencer <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={goNext}
                className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white transition-colors"
                style={{ backgroundColor: slide.color }}
                data-testid="next-btn"
              >
                Suivant <ChevronRight size={18} />
              </button>
            )}
          </div>
        </footer>

        {isPlaying && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-slate-800/80 backdrop-blur rounded-full border border-slate-700/50 z-50">
            <div className="flex items-center gap-0.5">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-amber-500 rounded-full"
                  style={{
                    height: `${8 + Math.random() * 10}px`,
                    animation: `audioBar 0.6s ease-in-out ${i * 0.15}s infinite alternate`,
                  }}
                />
              ))}
            </div>
            <span className="text-slate-400 text-xs">Narration en cours...</span>
          </div>
        )}
      </div>

      {/* ===== SECTIONS BELOW SLIDER ===== */}
      <div ref={sectionsRef}>
        {(showSections || isLast) && (
          <>
            <VisionSection />
            <QuoteCalculator />
            <FinalCTA />
          </>
        )}
      </div>

      {/* LEGAL-FOOTER-V1 */}
      <footer className="border-t border-slate-800 py-6 px-4 text-center text-slate-500 text-xs">
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/privacy" className="hover:text-white transition">Politique de confidentialite</a>
          <span>&middot;</span>
          <a href="/terms" className="hover:text-white transition">Conditions d'utilisation</a>
          <span>&middot;</span>
          <a href="/support" className="hover:text-white transition">Support</a>
        </div>
        <p className="mt-2">&copy; 2026 Propilote. Tous droits reserves.</p>
      </footer>

      <style>{`
        @keyframes audioBar {
          from { height: 6px; }
          to { height: 18px; }
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #f59e0b;
          cursor: pointer;
          border: 2px solid #1e293b;
        }
      `}</style>
    </div>
  );
}
